import { LOOT_POWER, MAZE, type NightmareType, TOMES, GLYPH_FRAGS } from '@somnhold/content';
import type { BattleParams } from '../battle/create.ts';
import { enemyMaxHp } from '../battle/combat.ts';
import { packSpots } from '../battle/maze.ts';
import { createMaze } from '../labyrinth.ts';
import { idiv, imax, imin, mulBp } from '../math/fixed.ts';
import { snapshot } from '../math/hash.ts';
import { deriveSeed, rngChance, rngInt, rngWeighted, seedRng } from '../math/rng.ts';
import type { BattleState, DungeonSpawn } from '../types.ts';
import { addRes, event, findPlayer, heroName } from './core.ts';
import { groupReached } from './eras.ts';
import { rollRelic } from './incursion.ts';
import { bigParty } from './party.ts';
import { giveItem, grantSeal, lootCtx, rollItem, lookOf } from './items.ts';
import { carried, gearMods, grantXp, addGlyphFragment, addPages } from './progress.ts';
import type { CampaignState, MazeRecord, Ms } from './types.ts';
import { tallyDeeds } from './deeds.ts';
import { tallyFeats } from './feats.ts';
import { logOuting } from './outings.ts';

/*
 * The Furrows (docs/LABERINTOS.md): from the second Marea, a labyrinth a week. The group's map lives in the
 * campaign; a run starts clean from it, and what it found and opened comes back when it ends (the server ran it).
 */

export const mazeOpen = (s: CampaignState): boolean => groupReached(s, MAZE.minGroup);

/** The campaign week a day falls in. */
export const mazeWeek = (day: number): number => idiv(day, MAZE.weekDays);

/** The week's labyrinth seed. */
export const mazeSeed = (s: CampaignState, week: number): number => deriveSeed(s.seed, 16000 + week);

const fresh = (week: number, players: number, old?: MazeRecord): MazeRecord => ({
  week,
  radius: MAZE.radiusBy[imin(MAZE.radiusBy.length - 1, imax(1, players))] ?? MAZE.radius,
  seen: [],
  gates: [],
  camps: [],
  chests: [],
  won: false,
  explorers: [],
  paid: [],
  runs: old?.runs ?? 0,
  wins: old?.wins ?? 0,
  ...(old?.carved ? { carved: old.carved } : {}),
  flawless: old?.flawless ?? 0,
});

/** The group's labyrinth for a week (a new week starts empty; the totals carry over). */
export const mazeRecord = (s: CampaignState, day: number): MazeRecord => {
  const week = mazeWeek(day);
  return s.maze && s.maze.week === week ? s.maze : fresh(week, s.players.filter((p) => !p.left).length, s.maze);
};

const partyOf = (s: CampaignState, pids: string[]): string[] => [...new Set(pids)].filter((pid) => !!findPlayer(s, pid)).sort().slice(0, MAZE.maxHeroes);

/**
 * A run of the Furrows for whoever is gathered (1 to 4 heroes): this week's labyrinth, the group's map of it, and
 * packs scaled to the party's size and level. Null before the second Marea.
 */
export const mazeParams = (s: CampaignState, pids: string[], day: number): BattleParams | null => {
  if (!mazeOpen(s)) return null;
  const party = partyOf(s, pids);
  const n = imax(1, party.length);
  const rec = mazeRecord(s, day);
  const week = rec.week;
  const lseed = mazeSeed(s, week);
  const radius = rec.radius ?? MAZE.radius;
  const { layout: L, map } = createMaze(lseed, radius);
  const seed = deriveSeed(lseed, 1 + (rec.runs % 4096));
  const rng = seedRng(seed ^ 0x3e2);
  let lv = 0;
  for (const pid of party) lv += findPlayer(s, pid)?.hero.level ?? 1;
  const avg = idiv(lv * 100, party.length || 1);
  const level = 10000 + idiv((avg - 100) * MAZE.perLevelBp, 100);
  const beasts = MAZE.bestiary;
  const entries = (Object.keys(beasts) as NightmareType[]).sort().map((t) => [t, beasts[t] as number] as const);
  const spawns: DungeonSpawn[] = [];
  const homes = [...L.packs, ...L.chests.filter((_, i) => L.guarded[i])];
  const packHp = MAZE.packHpBp[n] as number;
  homes.forEach((home, k) => {
    const spots = packSpots(L, home);
    if (k >= L.packs.length) {
      // A chest's guard: an inked elite of the bestiary.
      const kind = rngWeighted(rng, entries);
      const at = spots[0] as { q: number; r: number };
      spawns.push({ room: k, wave: 0, kind, tier: 2, elite: true, q: at.q, r: at.r, hpBp: bigParty(MAZE.guardHpBp, n) });
      return;
    }
    for (let j = 0; j < (MAZE.packSize[n] as number); j++) {
      const kind = rngWeighted(rng, entries);
      const at = spots[rngInt(rng, spots.length)] as { q: number; r: number };
      spawns.push({ room: k, wave: 0, kind, tier: rngChance(rng, MAZE.inkBp) ? 2 : 1, elite: false, q: at.q, r: at.r, ...(packHp !== 10000 ? { hpBp: packHp } : {}) });
    }
  });
  const heroes: BattleParams['heroes'] = party.map((pid, i) => {
    const p = findPlayer(s, pid) as NonNullable<ReturnType<typeof findPlayer>>;
    // What each carries into the Furrows: the crypt's preparations (they are its kind of fight).
    return { pid, name: heroName(p), cls: p.hero.heroClass, level: p.hero.level, control: 'live', powerBp: 10000, post: i, relics: p.hero.relics, awakened: p.hero.awakened ?? [], gear: gearMods(p.hero), ...lookOf(p.hero), ...carried(p.hero, 'crypt', s) };
  });
  const g = MAZE.guardian;
  const guardianHp = mulBp(enemyMaxHp(g.kind, 2, true), bigParty(g.hpBp, n));
  return snapshot({
    mode: 'dungeon',
    seed,
    map,
    structures: [],
    heroes,
    keepHp: 1,
    keepMaxHp: 1,
    marea: s.mareaIndex,
    wave: 0,
    spawns: [],
    dungeon: { spawns, bossHp: 0, dmgBp: level },
    maze: { week, seed: lseed, radius, seen: rec.seen, gates: rec.gates, camps: rec.camps, chests: rec.chests, centreWon: rec.won, party: n, guardianHp },
  });
};

/** How the table of the Furrows looks in the bastion: null until the centre has fallen once. */
export const furrowsTrophy = (
  s: CampaignState,
): { wins: number; flawless: number; posts: boolean; sash: boolean; carve: { seed: number; radius: number } } | null => {
  const m = s.maze;
  if (!m || m.wins < 1) return null;
  // Won before the table kept its week: the labyrinth of the week on record.
  const w = m.carved ?? { week: m.week, radius: m.radius ?? MAZE.radius };
  return { wins: m.wins, flawless: m.flawless, posts: m.wins >= MAZE.trophy.postsAt, sash: m.flawless > 0, carve: { seed: mazeSeed(s, w.week), radius: w.radius } };
};

export interface MazeResult {
  /** Crossings this run added to the group's map. */
  found: number;
  /** Chests it opened (and kept: a run where everyone fell loses them). */
  chests: number;
  /** Camps it pitched and gates it opened (new to the group's map). */
  camps: number;
  gates: number;
  /** The share of the labyrinth's crossings it added to the map (bp). */
  mapBp: number;
  /** The centre fell this run. */
  centre: boolean;
  /** Heroes who took the week's chest. */
  rewarded: string[];
}

const merge = (list: number[], flags: boolean[]): number[] => {
  const out = new Set(list);
  flags.forEach((f, i) => {
    if (f) out.add(i);
  });
  return [...out].sort((a, b) => a - b);
};

/**
 * A run of the Furrows is over (the server ran it). The group keeps what it mapped and opened, whatever happened;
 * the chests' spoils only if somebody walked out; the centre pays the week's chest to everyone who ran that week.
 */
export const applyMaze = (s: CampaignState, battle: BattleState, day: number, now: Ms): MazeResult => {
  tallyDeeds(s, 'maze', battle, now);
  // The Furrows are one of the hard places (docs/UNICAS.md §2.2).
  tallyFeats(s, battle, { hard: true }, now);
  const m = battle.dungeon?.maze;
  const pids = battle.heroes.map((h) => h.pid);
  const out: MazeResult = { found: 0, chests: 0, camps: 0, gates: 0, mapBp: 0, centre: false, rewarded: [] };
  if (!m) return out;
  const rec = mazeRecord(s, day);
  s.maze = rec;
  rec.runs++;
  // A run that began last week counts for nothing on this week's map.
  if (m.week !== rec.week) {
    event(s, now, 'maze', { data: { stale: true, party: pids.join(',') } });
    s.version++;
    return out;
  }
  const before = new Set(rec.seen);
  rec.seen = merge(rec.seen, m.seen);
  out.found = rec.seen.filter((i) => !before.has(i)).length;
  out.mapBp = idiv(out.found * 10000, imax(1, m.layout.nodes.length));
  out.gates = m.gates.filter((g, i) => g && !rec.gates.includes(i)).length;
  out.camps = m.camps.filter((c, i) => c && !rec.camps.includes(i)).length;
  rec.gates = merge(rec.gates, m.gates);
  rec.camps = merge(rec.camps, m.camps);
  const opened = m.openedBy.map((by, i) => (by && !rec.chests.includes(i) ? i : -1)).filter((i) => i >= 0);
  rec.chests = merge(rec.chests, m.chests);
  for (const pid of pids) if (!rec.explorers.includes(pid)) rec.explorers.push(pid);
  rec.explorers.sort();
  for (const pid of pids) grantXp(s, pid, out.found * MAZE.xpPerCrossing, now);
  // Somebody walked out (a rope, the torch, the centre): the chests' spoils come home.
  if (battle.result === 'won') {
    for (const i of opened) {
      out.chests++;
      addRes(s.storage, { wood: 0, stone: 0, crystal: 0, dust: MAZE.chest.dust });
      const p = findPlayer(s, m.openedBy[i] as string);
      if (p && rngChance(s.rng, MAZE.chest.itemBp)) giveItem(s, p, rollItem(s, s.rng, undefined, lootCtx(s, p, LOOT_POWER.mazeChest, 'maze', day)), now);
    }
  }
  if (m.outcome === 'centre' && !rec.won) {
    rec.won = true;
    rec.wins++;
    rec.carved = { week: rec.week, radius: rec.radius ?? MAZE.radius };
    if (m.falls === 0) rec.flawless++;
    out.centre = true;
    for (const pid of rec.explorers) {
      const p = findPlayer(s, pid);
      if (!p || p.left || rec.paid.includes(pid)) continue;
      rec.paid.push(pid);
      out.rewarded.push(pid);
      giveItem(s, p, rollItem(s, s.rng, 'rare', lootCtx(s, p, LOOT_POWER.mazeCenter, 'maze', day)), now);
      // The centre won: the seal of a unique's second awakening.
      grantSeal(s, p, 'furrows', now);
      grantXp(s, pid, MAZE.weekly.xp, now);
      addPages(s, pid, null, TOMES.expedition, now);
      addGlyphFragment(s, pid, 'expedition', GLYPH_FRAGS.expedition, now);
      rollRelic(s, pid, MAZE.weekly.relicBp, now);
    }
    rec.paid.sort();
    if (out.rewarded.length) addRes(s.storage, { wood: 0, stone: 0, crystal: 0, dust: MAZE.weekly.dust * out.rewarded.length });
  }
  logOuting(s, now, 'maze', pids, out.centre);
  event(s, now, 'maze', {
    data: { week: rec.week, outcome: m.outcome || 'fallen', found: out.found, mapBp: out.mapBp, camps: out.camps, gates: out.gates, chests: out.chests, centre: out.centre, falls: m.falls, party: pids.join(',') },
  });
  s.version++;
  return out;
};
